import { CheckboxCard, Group, Highlight, Stack, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useLoaderData } from "@remix-run/react";
import { IconMicroscope, IconTrash } from "@tabler/icons-react";
import { useMemo } from "react";

import { useTranslation } from "~/hooks/use-typed-translation";
import { loader } from "~/routes/($lang).lab/route";
import { formatCurrency } from "~/utils/format-currency";
import { shouldNameWrap } from "~/utils/should-name-wrap";

import classes from "./lab-card.module.scss";

interface LabCardProps {
  name: string;
  value: string;
  price: number | string;
  isChecked: boolean;
  search?: string;
}

// All units are in pixels
const microscopeIconSize = 45;
const cardInlinePadding = 12 * 2;
const trashIconSize = 28;
const innerGap = 12;
const totalUnavailableSpace =
  microscopeIconSize + cardInlinePadding + trashIconSize + innerGap;

export const LabCard = ({
  name,
  value,
  price,
  isChecked,
  search,
}: LabCardProps) => {
  // const [isChecked, setIsChecked] = useState(false);
  const { t, i18n } = useTranslation();
  const { ref, width } = useElementSize();

  const loaderData = useLoaderData<typeof loader>();
  const currency = loaderData?.currency;

  const shouldWrap = useMemo(() => {
    const availableSpace = width - totalUnavailableSpace;
    return shouldNameWrap(name, availableSpace);
  }, [width, name]);

  return (
    <CheckboxCard
      ref={ref}
      className={classes["checkbox-card"]}
      w={{ base: "100%", sm: "calc(50% - 1rem)" }}
      value={value}
      onClick={() => {
        // setIsChecked(a => !a);
      }}
      fz={13}>
      <Group gap={"0.75rem"} w="fit-content" align="start">
        <div className={classes["icon-wrapper"]}>
          <IconMicroscope size={23} stroke={1.5} color="white" />
        </div>
        <Stack
          gap={3}
          className={`${classes["text-wrapper"]} ${
            shouldWrap ? classes.wrap : ""
          }`}>
          <Highlight
            highlight={search || ""}
            fw={500}
            fz={15}
            className={`${classes.name} ${shouldWrap ? classes.wrap : ""}`}>
            {name}
          </Highlight>
          <Text w="fit-content" c="dimmed" fz={13}>
            {formatCurrency(+price, i18n.language, currency)}
          </Text>
        </Stack>
      </Group>

      {isChecked ? (
        <IconTrash
          className={classes["delete-icon"]}
          title={t("deselect-lab")}
          size={20}
          stroke={1.5}
          color="var(--mantine-color-red-6)"
        />
      ) : null}
    </CheckboxCard>
  );
};
